import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import LeafletMap from 'components/LeafletMap/LeafletMap'
import ScoreCard from 'components/ScoreCard/ScoreCard'
import React, { useState, useEffect } from 'react'
import styles from './Overview.scss'
import { Location } from 'components/LeafletMap/LeafletMap'
import { getAbbreviatedNumberString } from 'components/Income/Income'
import CircleIcon from '@mui/icons-material/Circle'
import ProductsByShops from 'containers/VerticalPage/ecommerce-extended/ProductsByShopTooltip/ProductsByShop'
import { useProductsByShops } from 'containers/VerticalPage/ecommerce-extended/ProductsByShopTooltip/useProductsByShop'
import { useSellerCartCheckMessage } from 'containers/VerticalPage/common/hooks'
import { getScoreCardValue } from 'containers/VerticalPage/ecommerce-extended/Overview/utils'
import { getMagnitudeConfig, getRiskConfig } from 'components/ecommerceExtended/EcommerceExtendedTable/util'
import { EcomExtendedSellerRegStatusEnum } from 'containers/EcommerceExtendedTablePage/constants'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import GenericText from 'components/common/GenericText'
import { InfoCircleOutlined } from '@ant-design/icons'
import { Select } from 'antd'
import AntdTooltip from 'UI/Tooltip/Tooltip'
import TaxProductsContainer from './TaxProductsContainer'

interface OverviewProps {
  magnitude: {
    score: number
    [key: string]: any
  }
  risk: {
    score: number
    [key: string]: any
  }
  locations: Location[] | []
  cartCheckStatus?: string
  setActiveTab: (value: number) => void
  setEmployeeIds: (value: number[]) => void
}

const Overview = ({ magnitude, risk, locations, cartCheckStatus, setEmployeeIds, setActiveTab }: OverviewProps) => {
  const [selectedShop, setSelectedShop] = useState<string>('')
  const [isTaxProductsExpanded, setIsTaxProductsExpanded] = useState(false)
  const productsByShops = useProductsByShops()
  const riskConfig = getRiskConfig(risk?.score) || null
  const magnitudeConfig = getMagnitudeConfig(magnitude.score) || null

  useEffect(() => {
    if (productsByShops && productsByShops.length > 0 && !selectedShop) {
      setSelectedShop(productsByShops[0].shopName)
    }
  }, [productsByShops, selectedShop])

  const magnitudeParameters = [
    {
      key: 'averageTrafficMonthly',
      label: 'Avg. Monthly WW Traffic',
      value:
        typeof magnitude?.averageTrafficMonthly === 'number'
          ? getAbbreviatedNumberString(magnitude?.averageTrafficMonthly)
          : 'N/A',
    },
    {
      key: 'averageLocalTrafficMonthly',
      label: 'Avg. Monthly Country Traffic',
      value:
        typeof magnitude?.averageLocalTrafficMonthly === 'number'
          ? getAbbreviatedNumberString(magnitude?.averageLocalTrafficMonthly)
          : 'N/A',
    },
    { key: 'yearsOfOperation', label: 'Years of Business', value: magnitude?.yearsOfOperation?.toLocaleString() },
    { key: 'numberOfShops', label: 'Number of Shops', value: magnitude?.numberOfShops?.toLocaleString() },
    {
      key: 'numberOfFollowers',
      label: 'Social Media Followers',
      value:
        typeof magnitude?.numberOfFollowers === 'number'
          ? getAbbreviatedNumberString(magnitude?.numberOfFollowers)
          : 'N/A',
    },
  ]

  const sellerCartCheck = useSellerCartCheckMessage(cartCheckStatus)

  const riskParameters = [
    {
      key: 'registrationStatus',
      label: 'Registry Status',
      value: (() => {
        switch (risk.registrationStatus) {
          case 'Registered':
            return (
              <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                <CheckIcon sx={{ color: '#34C759', fontSize: '16px' }} />
                <GenericText>{EcomExtendedSellerRegStatusEnum.Registered}</GenericText>
              </Stack>
            )
          case 'Unregistered':
            return (
              <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                <CloseIcon sx={{ color: '#FF3B30', fontSize: '16px' }} />
                <GenericText>{EcomExtendedSellerRegStatusEnum.Unregistered}</GenericText>
              </Stack>
            )
          case 'LateRegistration':
            return (
              <Stack sx={{ flexDirection: 'row', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                <GenericText>{EcomExtendedSellerRegStatusEnum.LateRegistration}</GenericText>
              </Stack>
            )
          case 'ExpiredRegistration':
            return risk.registrationStatus
          default:
            return null
        }
      })(),
    },
    {
      key: null,
      label: 'Magnitude Score',
      value: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <CircleIcon fontSize='small' style={{ fontSize: '12px', color: magnitudeConfig?.color }} />
          <span>{magnitudeConfig?.label}</span>
        </Box>
      ),
    },
    {
      key: 'physicalNexus.business',
      label: 'Physical Nexus (Address)',
      value: risk.physicalNexus?.business ? 'Detected' : 'Not Detected',
    },
    {
      key: 'physicalNexus.employees',
      label: 'Physical Nexus (Employees)',
      value: risk.physicalNexus?.employees ? 'Detected' : 'Not Detected',
    },
    ...(productsByShops && productsByShops.length > 0
      ? [
          {
            key: 'taxCheckedProducts',
            label: sellerCartCheck,
            value: (
              <Box
                onClick={() => setIsTaxProductsExpanded(!isTaxProductsExpanded)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  userSelect: 'none',
                  marginLeft: 'auto',
                }}
              >
                {isTaxProductsExpanded ? (
                  <ExpandLessIcon sx={{ color: '#1C2842' }} />
                ) : (
                  <ExpandMoreIcon sx={{ color: '#1C2842' }} />
                )}
              </Box>
            ),
          },
        ]
      : []),
    ...(isTaxProductsExpanded && productsByShops && productsByShops.length > 0
      ? [
          {
            key: 'taxProductsContainer',
            label: '',
            value: <TaxProductsContainer selectedShop={selectedShop} setSelectedShop={setSelectedShop} />,
          },
        ]
      : []),
  ]

  return (
    <Stack sx={{ height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
          gap: '24px',
          height: '100%',
          overflow: 'hidden',
          '&::-webkit-scrollbar': { width: '32px' },
          '&::-webkit-scrollbar-thumb': {
            borderWidth: '12px',
            borderStyle: 'solid',
            borderColor: 'rgba(0, 0, 0, 0)',
            backgroundClip: 'padding-box',
            borderRadius: '20px',
            backgroundColor: '#7E8BA6',
          },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#677187' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'flex-start',
            gap: '24px',
            height: '100%',
          }}
        >
          <ScoreCard
            title={
              <>
                <span>Ivix Magnitude</span>
                <AntdTooltip
                  title='Represents the operational scale of the seller, based on local & global traffic signals, social media followers, number of shops and years of operation.'
                  placement='bottom'
                  color='#fff'
                  header='Ivix Magnitude'
                >
                  <InfoCircleOutlined style={{ color: '#00000073', marginLeft: '6px' }} />
                </AntdTooltip>
              </>
            }
            subTitle='Magnitude'
            parameters={magnitudeParameters.map(({ key, label, value }) => ({
              parameter: label,
              html: (value && <>{value}</>) || <>{magnitude?.[key]}</> || null,
            }))}
            value={getScoreCardValue(magnitude?.score)}
            scoreColor={magnitudeConfig?.color}
            escalationLabel={magnitudeConfig?.label}
            isRiskCard={false}
          />
          <ScoreCard
            title={
              <>
                <span>IVIX Risk score</span>
                <AntdTooltip
                  title="A numerical risk score ranging from 0–100, where higher values indicate greater likelihood of non-compliance based on Tax collection compliance, seller registration status, seller's magnitude, and physical nexus address and employees."
                  placement='bottom'
                  color='#fff'
                  header='IVIX Risk score'
                >
                  <InfoCircleOutlined style={{ color: '#00000073', marginLeft: '6px' }} />
                </AntdTooltip>
              </>
            }
            subTitle='Risk'
            parameters={riskParameters.map(({ label, value, key }) => ({
              key: key || '',
              parameter: label,
              html: (value && <>{value}</>) || null,
            }))}
            value={getScoreCardValue(risk?.score)}
            escalationLabel={riskConfig?.label}
            scoreColor={riskConfig?.color}
            withEscalationLabel={true}
            isRiskCard={true}
          />
        </Box>
        <Box sx={{ width: '100%', height: '100%' }}>
          <Box className={styles.locationsContainer}>
            <h3>Locations</h3>
            <Box className={styles.legendContainer}>
              <div className={styles.legendRow}>
                <CircleIcon fontSize='small' style={{ fontSize: '10px', color: '#2257C5' }} />
                <span>Business Addresses</span>
                <AntdTooltip
                  title='Confirmed or probable address of the business based on web scraping, registry data, or marketplace listings'
                  placement='bottom'
                  color='#fff'
                  header='Business Addresses'
                >
                  <InfoCircleOutlined style={{ color: '#00000073' }} />
                </AntdTooltip>
              </div>
              <div className={styles.legendRow}>
                <CircleIcon fontSize='small' style={{ fontSize: '10px', color: '#FF5837' }} />
                <span>Employees Location</span>
                <AntdTooltip
                  title='Geographic distribution of known employees based on public data such as LinkedIn or corporate profiles.'
                  placement='bottom'
                  color='#fff'
                  header='Employees Location'
                >
                  <InfoCircleOutlined style={{ color: '#00000073' }} />
                </AntdTooltip>
              </div>
            </Box>
          </Box>
          <LeafletMap locations={locations} setActiveTab={setActiveTab} setEmployeeIds={setEmployeeIds} />
        </Box>
      </Box>
    </Stack>
  )
}

export default Overview
